<script setup lang="ts">
import { useFormValidation } from '../../../../composables/useFormValidation'
import { required } from '../../../../utils/validation'
import TextInput from '../../../../components/ui/form/TextInput.vue'
import CheckInput from '../../../../components/ui/form/CheckInput.vue'
import { api } from '../../../../api/Api'
import Toast from '../../../../services/Toast'
import { Permission } from '../../../../types/auth'
import FileUpload from '../../../../components/ui/form/FileUpload.vue'
import TextAreaInput from '../../../../components/ui/form/TextAreaInput.vue'
import SearchSelect from '../../../../components/ui/form/SearchSelect.vue'
import { ref, onMounted, computed } from 'vue'
import ModalFormTemplate from '../../../../components/ui/Templates/ModalFormTemplate.vue'

const props = defineProps<{
  data?: any | null
  mode: 'create' | 'edit' | 'view' | 'showQuickView' | 'toggleStatus'
  title: string | null
  endPoint: string,
  permissions?: Permission[],
  listCreateName?: string
}>()

const emit = defineEmits<{
  (e: 'save', data: any): void
  (e: 'close'): void
}>()

const { fields, formData, setFieldRules, validateForm, resetForm, setFormData } = useFormValidation({
  photo: props.data?.photo || '',
  // `icon` is now the SVG markup field (icon_image column was consolidated
  // into icon in the 2026_05_29 migration).
  icon: props.data?.icon || '',
  code: props.data?.code || '',
  name: props.data?.name || props.listCreateName || '',
  is_group: props.data?.is_group || false,
  parent_category_id: props.data?.parent_category_id || null,
  item_groups_id: props.data?.item_groups_id || null,
  description: props.data?.description || '',
  active: props.data?.active || true,
  uid: props.data?.uid || '',
})

const svgPreview = computed(() => {
  const raw = (fields.value.icon?.value || '').trim()
  if (!raw || !/^<svg[\s>]/i.test(raw)) return ''
  return raw
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
    .replace(/javascript\s*:/gi, '')
})

const svgInvalid = computed(() => {
  const raw = (fields.value.icon?.value || '').trim()
  return raw.length > 0 && !/^<svg[\s>]/i.test(raw)
})

setFieldRules('code', [required('Code is required')])
setFieldRules('name', [required('Name is required')])

const handleSubmit = async (close: boolean, newRecord: boolean) => {
  if (validateForm()) {
    const action = formData.value.uid != '' ? 'update' : 'create'
    const response = await api.post((props.endPoint.slice(-1) == '/' ? props.endPoint : props.endPoint + '/') + action, formData.value)
    if (response.success == true) {
      Toast.show('saved successfully')
      emit('save', response.data)
      if (close) {
        emit('close')
      } else if (newRecord) {
        resetForm()
      } else {
        setFormData(response.data)
      }
    }
  }
}

const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == 'view'
  if (props.mode == 'create') {
      resetForm()
    } else if (props.data) {
      setFormData(props.data)
    }
})


</script>
<template>
  <ModalFormTemplate :data="data" :mode="mode" :title="title" :endPoint="endPoint" :perm="permissions"
    :listCreateName="listCreateName" :tabList="[]" :formData="formData" @save="handleSubmit"
    @close="$emit('close')">
    <template #form>
      <div  class="grid md:grid-cols-2 gap-6">
        <div>
          <FileUpload label="Photo" accept=".pdf,.png,.jpg,.jpeg,.txt,.xls,.xlsx,.dox,.docx,.html,.xml" :max-size="5"
            :multiple="false" :readonly="isReadOnly" :image="true" path="item_categotries"
            v-model="fields.photo.value" size="mini" />
        </div>
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Icon (SVG)
          </label>
          <div class="mt-1 grid grid-cols-[1fr_auto] gap-3 items-start">
            <textarea
              v-model="fields.icon.value"
              :disabled="isReadOnly"
              rows="6"
              placeholder='Paste an SVG, e.g. <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">...</svg>'
              class="appearance-none block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-xs font-mono transition-colors"
              :class="[
                svgInvalid
                  ? 'border-red-300 dark:border-red-700'
                  : 'border-gray-300 dark:border-gray-600'
              ]"
            />
            <div
              class="w-20 h-20 flex items-center justify-center border border-dashed border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-800 text-gray-400 overflow-hidden p-2"
              aria-label="SVG preview"
            >
              <div v-if="svgPreview" v-html="svgPreview" class="w-full h-full [&_svg]:w-full [&_svg]:h-full" />
              <span v-else class="text-xs">Preview</span>
            </div>
          </div>
          <p v-if="svgInvalid" class="mt-1 text-sm text-red-600 dark:text-red-400">
            Content must start with &lt;svg&gt;.
          </p>
        </div>
        <TextInput v-model="fields.code.value" label="Code" placeholder="Enter Code" :readonly="isReadOnly"
          :error="fields.code.touched ? fields.code.error : null" @blur="fields.code.touched = true" />

        <TextInput v-model="fields.name.value" label="Name" placeholder="Enter Name" :readonly="isReadOnly"
          :error="fields.name.touched ? fields.name.error : null" @blur="fields.name.touched = true" />

          <SearchSelect
          v-model="fields.item_groups_id.value"
          end-point="inventory/item-management/item-groups"
          label="Item Group"
          :readonly="isReadOnly"
          :error="
            fields.item_groups_id.touched
              ? fields.item_groups_id.error
              : null
          "
          @blur="fields.item_groups_id.touched = true"
        />

          <CheckInput v-model="fields.is_group.value" :readonly="isReadOnly" label="is Group"
          :error="fields.is_group.touched ? fields.is_group.error : null" @blur="fields.is_group.touched = true" />

          <SearchSelect
          v-model="fields.parent_category_id.value"
          end-point="inventory/item-management/item-categories"
          :require-params="true"
          :params="[
            { field: 'is_group', value: true, required: true },
            { field: 'item_groups_id', value: fields.item_groups_id.value, required: true },
          ]"
          label="Parent Category"
          v-if="!fields.is_group.value"
          :readonly="isReadOnly"
          :error="
            fields.parent_category_id.touched
              ? fields.parent_category_id.error
              : null
          "
          @blur="fields.parent_category_id.touched = true"
        />

        <TextAreaInput v-model="fields.description.value" label="Description" :rows="4" :readonly="isReadOnly" :multiple="true"
          :error="fields.description.touched ? fields.description.error : null"
          @blur="fields.description.touched = true" />

        <CheckInput v-model="fields.active.value" :readonly="isReadOnly" label="Active"
          :error="fields.active.touched ? fields.active.error : null" @blur="fields.active.touched = true" />

      </div>
    </template>
  </ModalFormTemplate>
</template>